import { Component, inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { TituloRotinaComponent } from '../shared/components/redesign/titulo-rotina/titulo-rotina.component';
import { SearchComponent } from '../shared/components/redesign/search/search.component';
import { ButtonComponent } from '../shared/components/redesign/botao/botao.component';
import { TabelaComponent } from '../shared/components/redesign/tabela/tabela.component';
import { TabelaAcaoComponent, ItemMenuAcao } from '../shared/components/redesign/tabela-acao/tabela-acao.component';
import { PaginadorComponent } from '../shared/components/redesign/paginador/paginador.component';
import { FiltrosTabComponent, FiltroTabItem } from '../shared/components/redesign/filtros-tab/filtros-tab.component';
import { CampoSelectComponent } from '../shared/components/redesign/campo-select/campo-select.component';
import { CampoDataComponent } from '../shared/components/redesign/campo-data/campo-data.component';
import { FiltroPainelComponent } from '../shared/components/redesign/filtro-painel/filtro-painel.component';
import { PageEvent } from '@angular/material/paginator';
import { MenuMobileComponent } from '../shared/components/redesign/menu-mobile/menu-mobile.component';
import { CelularMaskPipe } from '../shared/pipe/celular-mask.pipe';
import { ContaService } from '../shared/service/conta.service';
import { Conta } from '../shared/model/conta.model';
import { ContaFiltro } from '../shared/model/conta-filtro.model';
import { CanalOrigemContatoEnum } from '../shared/enum/canal-origem-contato.enum';
import { ModalCadastroContaComponent } from './modal-cadastro-conta/modal-cadastro-conta.component';
import { MsgService } from '../shared/service/msg.service';
import { FluxoService } from '../shared/service/fluxo-service';
import { FluxoSelect } from '../shared/model/fluxo/fluxo-select-model';
import { Setor } from '../shared/model/setor/setor.model';
import { SetorService } from '../shared/service/setor.service';
import { UsuarioService } from '../shared/service/usuario.service';
import { UsuarioResumo } from '../shared/model/usuario/usuario-resumo-model';

type AbaConta = 'TODOS' | 'ATIVOS' | 'INATIVOS';

interface OpcaoCanal {
  id: CanalOrigemContatoEnum;
  nome: string;
}

@Component({
  selector: 'app-conta',
  standalone: true,
  imports: [
    MatTableModule,
    MatTooltip,
    DatePipe,
    ReactiveFormsModule,
    TituloRotinaComponent,
    SearchComponent,
    ButtonComponent,
    TabelaComponent,
    TabelaAcaoComponent,
    PaginadorComponent,
    FiltrosTabComponent,
    CampoSelectComponent,
    CampoDataComponent,
    FiltroPainelComponent,
    CelularMaskPipe
  ],
  templateUrl: './conta.component.html',
  styleUrl: './conta.component.css'
})
export class ContaComponent implements OnInit {
  readonly CanalOrigemContatoEnum = CanalOrigemContatoEnum;

  contas: Conta[] = [];
  dataSource = new MatTableDataSource<Conta>();
  carregando = false;

  totalGeral = 0;
  totalAtivos = 0;
  totalInativos = 0;

  displayedColumns: string[] = [
    'gutterInicio', 'acao', 'nome', 'numero', 'wabaOuIgId', 'metaId', 'aplicativoId',
    'setor', 'fluxo', 'status', 'gutterFim'
  ];

  page = 0;
  limit = 10;
  length = 0;
  pageSizeOptions = [10, 25, 50, 100];

  abaAtiva: AbaConta = 'TODOS';
  nomeFiltro?: string;
  private readonly searchSubject = new Subject<string>();

  painelFiltroAberto = false;
  readonly opcoesCanal: OpcaoCanal[] = [
    { id: CanalOrigemContatoEnum.WHATSAPP, nome: 'WhatsApp' },
    { id: CanalOrigemContatoEnum.INSTAGRAM, nome: 'Instagram' }
  ];

  fluxos: FluxoSelect[] = [];
  setores: Setor[] = [];
  readonly opcaoTodosSetor = { id: 0, nome: 'Todos' } as unknown as Setor;
  readonly opcaoTodosFluxo = { id: 0, nome: 'Todos' } as unknown as FluxoSelect;

  get setoresComTodos(): Setor[] {
    return [this.opcaoTodosSetor, ...this.setores];
  }

  get fluxosComTodos(): FluxoSelect[] {
    return [this.opcaoTodosFluxo, ...this.fluxos];
  }
  usuarios: UsuarioResumo[] = [];

  private readonly contaService = inject(ContaService);
  private readonly dialog = inject(MatDialog);
  private readonly msgService = inject(MsgService);
  private readonly location = inject(Location);
  private readonly fluxoService = inject(FluxoService);
  private readonly setorService = inject(SetorService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly fb = inject(FormBuilder);

  filtroForm = this.fb.group({
    canal: [[] as OpcaoCanal[]],
    setor: [[] as Setor[]],
    fluxo: [[] as FluxoSelect[]],
    dataCriacao: [null as any],
    dataAlteracao: [null as any],
    usuarioCriacao: [null as UsuarioResumo | null],
    usuarioAlteracao: [null as UsuarioResumo | null]
  });

  voltar() {
    this.location.back();
  }

  ngOnInit() {
    this.configurarDebounce();
    this.buscarContas();
    this.buscarOpcoesFiltro();
    this.filtroForm.get('setor')!.valueChanges.subscribe(valor => this.aplicarSelecaoTodos('setor', valor ?? [], this.setores));
    this.filtroForm.get('fluxo')!.valueChanges.subscribe(valor => this.aplicarSelecaoTodos('fluxo', valor ?? [], this.fluxos));
    this.filtroForm.valueChanges.subscribe(() => this.zerarPaginacao());
  }

  private configurarDebounce() {
    this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe(valor => {
      this.nomeFiltro = valor?.trim() || undefined;
      this.zerarPaginacao();
    });
  }

  private aplicarSelecaoTodos(campo: 'setor' | 'fluxo', valor: { id: number }[], opcoes: { id: number }[]) {
    if (!valor.some(item => item?.id === 0)) {
      return;
    }
    const semTodos = valor.filter(item => item?.id !== 0);
    const todosJaSelecionados = semTodos.length === opcoes.length;
    this.filtroForm.get(campo)!.setValue(todosJaSelecionados ? [] : [...opcoes] as any);
  }

  private buscarOpcoesFiltro() {
    this.fluxoService.buscarFluxoSelect().subscribe(res => this.fluxos = res);
    this.setorService.buscarSetoresSelect().subscribe((res: Setor[]) => this.setores = res);
    this.usuarioService.buscarUsuarios().subscribe(res => this.usuarios = res);
  }

  buscarContas() {
    this.carregando = true;
    const valores = this.filtroForm.value;

    const filtroBase: ContaFiltro = {
      nome: this.nomeFiltro,
      canalOrigem: valores.canal?.length ? valores.canal.map(c => c.id) : undefined,
      setorId: valores.setor?.length ? valores.setor.map(s => s.id).filter(id => id !== 0) : undefined,
      fluxoId: valores.fluxo?.length ? valores.fluxo.map(f => f.id).filter(id => id !== 0) : undefined,
      dataCriacao: valores.dataCriacao?.format ? valores.dataCriacao.format('YYYY-MM-DD') : undefined,
      dataAlteracao: valores.dataAlteracao?.format ? valores.dataAlteracao.format('YYYY-MM-DD') : undefined,
      usuarioCriacaoId: valores.usuarioCriacao?.id,
      usuarioAlteracaoId: valores.usuarioAlteracao?.id
    };

    const filtroPaginado: ContaFiltro = {
      ...filtroBase,
      ativo: this.abaAtiva === 'ATIVOS' ? true : this.abaAtiva === 'INATIVOS' ? false : undefined,
    };

    this.contaService.buscarTodosNumerosFiltrado(filtroPaginado, this.page, this.limit).subscribe({
      next: res => {
        this.contas = res.content as Conta[];
        this.dataSource.data = this.contas;
        this.length = res.totalElements;
      },
      complete: () => this.carregando = false
    });

    this.contaService.buscarTodosNumerosFiltrado(filtroBase, 0, 1).subscribe({
      next: res => {
        this.totalGeral = res.totalElements;
      }
    });
    this.contaService.buscarTodosNumerosFiltrado({ ...filtroBase, ativo: true }, 0, 1).subscribe({
      next: res => { this.totalAtivos = res.totalElements; }
    });
    this.contaService.buscarTodosNumerosFiltrado({ ...filtroBase, ativo: false }, 0, 1).subscribe({
      next: res => { this.totalInativos = res.totalElements; }
    });
  }

  get abasFiltro(): FiltroTabItem[] {
    return [
      { valor: 'TODOS', label: 'Todos', contador: this.totalGeral },
      { valor: 'ATIVOS', label: 'Ativos', contador: this.totalAtivos, corBolinha: 'var(--verde-padrao)' },
      { valor: 'INATIVOS', label: 'Inativos', contador: this.totalInativos, corBolinha: 'var(--vermelho-padrao)' },
    ];
  }

  selecionarAba(aba: AbaConta) {
    this.abaAtiva = aba;
    this.zerarPaginacao();
  }

  zerarPaginacao() {
    this.page = 0;
    this.buscarContas();
  }

  alternarPainelFiltro() {
    this.painelFiltroAberto = !this.painelFiltroAberto;
  }

  get temFiltrosAplicados(): boolean {
    return this.tagsFiltros.length > 0;
  }

  get tagsFiltros(): { chave: string; texto: string }[] {
    const v = this.filtroForm.value;
    const tags: { chave: string; texto: string }[] = [];
    (v.canal ?? []).forEach(c => tags.push({ chave: `canal:${c.id}`, texto: `Origem: ${c.nome}` }));
    this.tagsMultiplas('setor', 'Setor', v.setor ?? [], this.setores).forEach(t => tags.push(t));
    this.tagsMultiplas('fluxo', 'Fluxo', v.fluxo ?? [], this.fluxos).forEach(t => tags.push(t));
    if (v.dataCriacao?.format) tags.push({ chave: 'dataCriacao', texto: `Data Criação: ${v.dataCriacao.format('DD/MM/YYYY')}` });
    if (v.dataAlteracao?.format) tags.push({ chave: 'dataAlteracao', texto: `Data Alteração: ${v.dataAlteracao.format('DD/MM/YYYY')}` });
    if (v.usuarioCriacao) tags.push({ chave: 'usuarioCriacao', texto: `Usuário Criação: ${v.usuarioCriacao.nome}` });
    if (v.usuarioAlteracao) tags.push({ chave: 'usuarioAlteracao', texto: `Usuário Alteração: ${v.usuarioAlteracao.nome}` });
    return tags;
  }

  private tagsMultiplas(campo: string, rotulo: string, selecionados: { id: number; nome?: string }[], opcoes: unknown[]) {
    if (selecionados.length > 0 && selecionados.length === opcoes.length) {
      return [{ chave: `${campo}:todos`, texto: `${rotulo}: Todos` }];
    }
    return selecionados.map(item => ({ chave: `${campo}:${item.id}`, texto: `${rotulo}: ${item.nome}` }));
  }

  private readonly camposMultiplos = ['canal', 'setor', 'fluxo'];

  removerFiltro(chave: string) {
    const [campo, id] = chave.split(':');
    if (this.camposMultiplos.includes(campo)) {
      if (id === 'todos') {
        this.filtroForm.get(campo)?.setValue([]);
        return;
      }
      const atual: { id: any }[] = this.filtroForm.get(campo)?.value ?? [];
      this.filtroForm.get(campo)?.setValue(atual.filter(item => String(item.id) !== id));
      return;
    }
    this.filtroForm.get(chave)?.setValue(null);
  }

  handlePageEvent(e: PageEvent) {
    this.page = e.pageIndex;
    this.limit = e.pageSize;
    this.buscarContas();
  }

  filtrarBusca(valor: string) {
    this.searchSubject.next(valor);
  }

  logoCanal(conta: Conta): string {
    return conta.canalOrigem === CanalOrigemContatoEnum.INSTAGRAM
      ? './assets/logos/ig-logo-nova.svg'
      : './assets/logos/wpp-logo.png';
  }

  nomeCanal(conta: Conta): string {
    return conta.canalOrigem === CanalOrigemContatoEnum.INSTAGRAM ? 'Instagram' : 'WhatsApp';
  }

  wabaOuIgId(conta: Conta): string {
    if (conta.canalOrigem === CanalOrigemContatoEnum.INSTAGRAM) {
      return conta.numeroMetaId || '--';
    }
    return conta.whatsappBussinesId || '--';
  }

  setoresNomes(setor: any): string {
    const lista = Array.isArray(setor) ? setor : (setor ? [setor] : []);
    return lista.length > 0 ? lista.map((s: any) => s.nome).join(', ') : '--';
  }

  itensAcao(elemento: Conta): ItemMenuAcao[] {
    return [
      { label: 'Visualizar', aoClicar: () => this.abrirModal(true, elemento) },
      { label: 'Editar', aoClicar: () => this.abrirModal(false, elemento) }
    ];
  }

  abrirModal(visualizar: boolean, conta?: Conta) {
    const mobile = window.innerWidth <= 970;
    const posicao = mobile ? { bottom: '0', left: '0' } : { top: '0', right: '0' };

    const dialogRef = this.dialog.open(ModalCadastroContaComponent, {
      width: mobile ? '100%' : '450px',
      maxWidth: mobile ? '100vw' : '450px',
      height: mobile ? '92dvh' : '100dvh',
      maxHeight: mobile ? '92dvh' : '100dvh',
      position: posicao,
      data: { visualizar, conta: conta ?? new Conta() },
      panelClass: mobile ? 'slide-modal-baixo' : 'slide-modal',
      disableClose: !visualizar
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.contaService.salvarEditarNumero(res).subscribe({
          next: salvo => {
            this.msgService.msgSucesso(salvo.id ? 'Conta editada com sucesso!' : 'Conta criada com sucesso!');
            this.buscarContas();
          },
          error: err => this.msgService.msgErro(err.message)
        });
      }
    });
  }

  abrirMenuMobile() {
    this.dialog.open(MenuMobileComponent, {
      width: '85%',
      maxWidth: '320px',
      height: '100dvh',
      maxHeight: '100dvh',
      position: { top: '0', left: '0' },
      panelClass: 'slide-modal-esquerda'
    });
  }
}
